/**
 * TrendsSection — секция «Тренды» дашборда (GET /admin/dashboard/trends):
 * MRR по месяцам, активные клиенты соло/клиника стеком (+текущий срез),
 * заявки → клиенты. Самодостаточна: свой запрос за 6 месяцев.
 */

import type {ReactNode} from 'react';
import {useQuery} from '@tanstack/react-query';

import {dashboardTrendsQueryOptions} from '@services/dashboard';
import {Loader} from '@components/ui/Loader';

import {MonthBars, type MonthBarsSeries} from '../MonthBars';
import {SplitBar} from '../SplitBar';

const MRR_SERIES: MonthBarsSeries[] = [{key: 'value', label: 'MRR, сом', color: '#2C5CDB'}];

const ACTIVE_SERIES: MonthBarsSeries[] = [
  {key: 'solo', label: 'Соло', color: '#2C5CDB', stackId: 'a'},
  {key: 'clinic', label: 'Клиники', color: '#27A683', stackId: 'a'},
];

const LEADS_SERIES: MonthBarsSeries[] = [
  {key: 'leads', label: 'Заявки', color: '#C9D4EC'},
  {key: 'clients', label: 'Стали клиентами', color: '#2C5CDB'},
];

export const TrendsSection = () => {
  const {data, isLoading} = useQuery(dashboardTrendsQueryOptions(6));

  return (
    <section>
      <h2 className="mb-4 text-[15px] font-semibold text-skr-ink">Тренды · 6 месяцев</h2>
      {isLoading || !data ? (
        <div className="flex min-h-[230px] items-center justify-center">
          <Loader label="Загружаем тренды…" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1">
          <TrendCard title="MRR по месяцам" legend={MRR_SERIES}>
            {data.mrr.every((p) => p.value === 0) ? (
              <Empty>Биллинг ещё не запущен — MRR появится с первыми оплатами</Empty>
            ) : (
              <MonthBars data={data.mrr} series={MRR_SERIES} />
            )}
          </TrendCard>

          <TrendCard title="Активные клиенты" legend={ACTIVE_SERIES}>
            <MonthBars data={data.activeClients} series={ACTIVE_SERIES} />
            <div className="mt-3 border-t border-skr-hairline pt-3">
              <SplitBar solo={data.soloClinicRatio.solo} clinic={data.soloClinicRatio.clinic} />
            </div>
          </TrendCard>

          <TrendCard title="Заявки → клиенты" legend={LEADS_SERIES}>
            <MonthBars data={data.leadsToClients} series={LEADS_SERIES} />
          </TrendCard>
        </div>
      )}
    </section>
  );
};

const TrendCard = ({
  title,
  legend,
  children,
}: {
  title: string;
  legend: MonthBarsSeries[];
  children: ReactNode;
}) => (
  <div className="rounded-[15px] border border-skr-line bg-card px-[18px] pb-4 pt-[15px]">
    <div className="mb-3 flex items-center justify-between gap-2">
      <div className="text-[13.5px] font-semibold">{title}</div>
      {legend.length > 1 && (
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {legend.map((s) => (
            <span key={s.key} className="flex items-center gap-[5px]">
              {/* цвет серии приходит из конфига чарта */}
              <span className="size-2 rounded-full" style={{background: s.color}} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
    {children}
  </div>
);

const Empty = ({children}: {children: ReactNode}) => (
  <div className="flex h-[168px] items-center justify-center px-6 text-center text-[12.5px] leading-[1.5] text-muted-foreground">
    {children}
  </div>
);
